import React, { useCallback, useEffect, useState } from "react";
import { AccountBookOutlined, ReloadOutlined } from "@ant-design/icons";
import { Card, Col, Row, Skeleton, Tag, Typography } from "antd";
import { Client } from "../../core/api-client";
import { API_GET_SALES_LIST } from "../../shared/routes/private.routes";
import DashboardLoadError from "../dashboard/components/dashboard-load-error.component";
import { isFailedResponse } from "../dashboard/dashboard-response";
import "../../assets/styles/dashboard.less";

const { Title, Text } = Typography;

type WorkflowStatus = "PIPELINE" | "WON" | "LOST";

type SalesCounts = {
    pipeline: number;
    won: number;
    lost: number;
};

const EMPTY_COUNTS: SalesCounts = {
    pipeline: 0,
    won: 0,
    lost: 0,
};

const SalesDashboardPage: React.FC = () => {
    const [counts, setCounts] = useState<SalesCounts>(EMPTY_COUNTS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const loadSalesCounts = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const statuses: WorkflowStatus[] = ["PIPELINE", "WON", "LOST"];

            const responses = await Promise.all(
                statuses.map((workflowStatus) =>
                    Client.post(
                        API_GET_SALES_LIST,
                        { workflowStatus },
                        {
                            params: {
                                page: 1,
                                limit: 1,
                                sort_by: "",
                                sort_type: "DESC",
                            },
                        }
                    )
                )
            );

            if (responses.some(isFailedResponse)) {
                setError(true);
                return;
            }

            setCounts({
                pipeline: Number(responses[0]?.data?.total ?? 0),
                won: Number(responses[1]?.data?.total ?? 0),
                lost: Number(responses[2]?.data?.total ?? 0),
            });
        } catch (error) {
            console.error("Failed to load Sales Dashboard counts", error);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadSalesCounts();
    }, [loadSalesCounts]);

    const total = counts.pipeline + counts.won + counts.lost;

    return (
        <div className="app-page dashboard-page">
            <div className="app-page-header dashboard-page-header">
                <div>
                    <div className="app-page-title-wrap">
                        <span className="app-page-icon">
                            <AccountBookOutlined />
                        </span>
                        <Title level={1} className="app-page-title">
                            Sales Dashboard
                        </Title>
                    </div>
                    <p className="app-page-subtitle">Sales overview</p>
                </div>

                <button
                    className="dashboard-refresh-button"
                    type="button"
                    onClick={() => void loadSalesCounts()}
                    disabled={loading}
                    aria-label="Refresh sales dashboard"
                >
                    <ReloadOutlined />
                    <span>Refresh</span>
                </button>
            </div>

            {error ? (
                <DashboardLoadError
                    message="Unable to load sales dashboard data."
                    onRetry={() => void loadSalesCounts()}
                />
            ) : (
            <Row gutter={[16, 16]}>
                <Col xs={24}>
                    <Card className="dashboard-summary-card" variant="borderless">
                        {loading ? (
                            <Skeleton active paragraph={{ rows: 3 }} />
                        ) : (
                            <>
                                <div className="dashboard-summary-top">
                                    <div className="dashboard-summary-main">
                                        <span className="dashboard-summary-icon">
                                            <AccountBookOutlined />
                                        </span>
                                        <Text className="dashboard-summary-title">
                                            Total Deals
                                        </Text>
                                    </div>
                                    <div className="dashboard-summary-total">{total}</div>
                                </div>

                                <div className="dashboard-status-grid">
                                    <div className="dashboard-status-item">
                                        <Tag color="processing">Sales Pipeline</Tag>
                                        <strong>{counts.pipeline}</strong>
                                    </div>
                                    <div className="dashboard-status-item">
                                        <Tag color="success">Won</Tag>
                                        <strong>{counts.won}</strong>
                                    </div>
                                    <div className="dashboard-status-item">
                                        <Tag color="error">Lost</Tag>
                                        <strong>{counts.lost}</strong>
                                    </div>
                                </div>
                            </>
                        )}
                    </Card>
                </Col>
            </Row>
            )}
        </div>
    );
};

export default SalesDashboardPage;
